/** Movement ledger and a project's material. */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Paginator } from "@primereact/ui/paginator";
import { Button } from "../../components/ui/Button.js";
import { FilterTabs, SearchBox } from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import { apiGet } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import {
  ErrorPanel,
  LoadingPanel,
  formatDate,
  formatMoney,
} from "../shared.js";
import {
  LocationFilter,
  type Movement,
  OPERATION_ICON,
  type Operation,
  PAGE_SIZE,
  type Page,
  StockOperationDialog,
} from "./stock-operations.js";

export const MOVEMENT_TYPES = [
  "receipt",
  "issue",
  "return",
  "transfer_in",
  "transfer_out",
  "adjustment_in",
  "adjustment_out",
] as const;

/** Signed quantity as it affects the location's stock. */
export function signedQuantity(movement: Movement) {
  const outgoing =
    movement.type === "issue" ||
    movement.type === "transfer_out" ||
    movement.type === "adjustment_out";
  const quantity = Number(movement.quantity);
  return outgoing ? -Math.abs(quantity) : quantity;
}

export function MovementList({
  projectId,
  compact,
}: {
  projectId?: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [locationId, setLocationId] = useState("");
  const movements = useQuery({
    queryKey: ["inventory-movements", projectId, page, type, q, locationId],
    queryFn: () =>
      apiGet<Page<Movement>>("/inventory/movements", {
        query: {
          projectId,
          page,
          pageSize: PAGE_SIZE,
          type: type || undefined,
          q: q || undefined,
          locationId: locationId || undefined,
        },
      }),
    placeholderData: (previous) => previous,
  });
  const rows = movements.data?.data ?? [];
  return (
    <div className="space-y-3">
      {!compact ? (
        <>
          <FilterTabs
            value={type}
            onChange={(value) => {
              setType(value);
              setPage(1);
            }}
            options={[
              { value: "", label: t("common.all") },
              ...MOVEMENT_TYPES.map((value) => ({
                value,
                label: t(`inventory.movement.${value}`),
              })),
            ]}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_16rem]">
            <SearchBox
              value={q}
              onChange={(value) => {
                setQ(value);
                setPage(1);
              }}
            />
            <LocationFilter
              value={locationId}
              onChange={(value) => {
                setLocationId(value);
                setPage(1);
              }}
            />
          </div>
        </>
      ) : null}
      {movements.isLoading ? (
        <LoadingPanel />
      ) : movements.error ? (
        <ErrorPanel error={movements.error} />
      ) : rows.length ? (
        <ul className="divide-y divide-app-border rounded-xl border border-app-border bg-app-surface text-sm">
          {rows.map((movement) => {
            const quantity = signedQuantity(movement);
            return (
              <li
                key={movement.id}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5"
              >
                <span className="w-24 text-app-muted">
                  {formatDate(movement.createdAt)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium">
                    {movement.catalogItem.name}
                  </span>
                  <span className="ml-2 text-xs text-app-muted">
                    {t(`inventory.movement.${movement.type}`)} ·{" "}
                    {movement.location.code}
                    {movement.note ? ` · ${movement.note}` : ""}
                  </span>
                  {movement.projectId && !projectId ? (
                    <Link
                      className="ml-2 text-xs text-brand hover:underline"
                      to={`/projects/${movement.projectId}`}
                    >
                      {t("projects.project")}
                    </Link>
                  ) : null}
                </span>
                <span
                  className={cx(
                    "w-20 text-right font-medium tabular-nums",
                    quantity < 0 ? "text-danger" : "text-success",
                  )}
                >
                  {quantity > 0 ? "+" : ""}
                  {quantity.toLocaleString()}
                </span>
                <span className="w-24 text-right tabular-nums text-app-muted">
                  {movement.unitCost ? formatMoney(movement.unitCost) : "—"}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rounded-xl border border-dashed border-app-border p-8 text-center text-sm text-app-muted">
          {t("inventory.noMovements")}
        </p>
      )}
      {(movements.data?.total ?? 0) > PAGE_SIZE ? (
        <Paginator.Root
          page={page}
          total={movements.data?.total ?? 0}
          itemsPerPage={PAGE_SIZE}
          onPageChange={(event: { value: number }) => setPage(event.value)}
        />
      ) : null}
    </div>
  );
}

/** Material issued to a project, with issue/return actions. */
export function ProjectMaterial({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const [operation, setOperation] = useState<Operation | null>(null);
  if (!has("inventory.view")) return null;
  return (
    <div className="space-y-3">
      {canManage && has("inventory.issue") ? (
        <div className="flex gap-2">
          <Button
            type="button"
            size="small"
            icon={OPERATION_ICON.issue}
            onClick={() => setOperation("issue")}
          >
            {t("inventory.op.issue")}
          </Button>
          <Button
            type="button"
            size="small"
            variant="outlined"
            severity="secondary"
            icon={OPERATION_ICON.return}
            onClick={() => setOperation("return")}
          >
            {t("inventory.op.return")}
          </Button>
        </div>
      ) : null}
      <MovementList projectId={projectId} compact />
      {operation ? (
        <StockOperationDialog
          operation={operation}
          projectId={projectId}
          onClose={() => setOperation(null)}
        />
      ) : null}
    </div>
  );
}
